import { useRouter } from 'next/router'

import * as S from './styles'
import { Image } from '../image'
import { Tag } from '../tag'
import { Text } from '../text'
import { Author } from '@/service/api/blog'

export type BlogCardProps = {
  /**
   * Blog post slug
   *
   * @example
   * ```tsx
   *
   * <BlogCard slug='my-awesome-article' />
   * ```
   *
   */
  slug: string

  /**
   * Card Image src
   *
   * @example
   * ```tsx
   * import { NhShape } from '@/assets/imgs'
   *
   * <BlogCard src={NhShape} />
   * ```
   *
   */
  src: string

  /**
   * Card Image alt
   *
   * @example
   * ```tsx
   * <BlogCard alt="card image" />
   * ```
   *
   */
  alt: string

  /**
   * Card Title
   *
   * @example
   * ```tsx
   * <BlogCard title="This should be the title" />
   * ```
   *
   */
  title: string

  /**
   * Card Author
   *
   * @example
   * ```tsx
   * <BlogCard author={author} />
   * ```
   *
   */
  author: Author

  /**
   * Class name
   *
   * @example
   * ```tsx
   * <BlogCard className="avatar" />
   * ```
   *
   */
  className?: string

  /**
   * Post tags
   *
   * @example
   * ```tsx
   * <BlogCard tags={['Aplicativo', 'Desenvolvimento']} />
   * ```
   *
   */
  tags?: string[]

  /**
   * Function to be executed when the card is clicked
   * @example
   * ```tsx
   * <BlogCard onClick={() => console.log('clicked')} />
   * ```
   * */
  onClick?: (
    id: string,
    event?: React.MouseEvent<HTMLButtonElement, MouseEvent>,
  ) => void

  /**
   * Filename of the image
   * @example
   * ```tsx
   * <BlogCard fileName="my-image" />
   * ```
   * */
  fileName?: string
  publishedAt: string
}

export const BlogCard: React.FC<BlogCardProps> = ({
  slug,
  src,
  alt,
  title,
  author,
  className,
  tags,
  fileName,
  publishedAt,
}) => {
  const router = useRouter()

  const handleClick = async (postSlug: string) => {
    await router.push(`/blog/${postSlug}`)
  }

  return (
    <S.BlogCard className={className} onClick={() => handleClick(slug)}>
      <div className="image-container">
        <Image src={src} alt={alt} fileName={fileName} fill />
      </div>

      <div className="text-container">
        <Text size="b2" color="black" className="title">
          {title}
        </Text>

        <S.TagContainer>
          {tags?.map((tag) => (
            <Tag key={tag} label={tag} />
          ))}
        </S.TagContainer>

        <Text size="b3" color="darkgray" className="author">
          {author.name} {new Date(publishedAt).toLocaleDateString('pt-BR')}
        </Text>
      </div>
    </S.BlogCard>
  )
}
